import { InputHTMLAttributes, forwardRef } from 'react';

import { useTranslation } from 'react-i18next';

import { Skeleton } from 'shared-components';
import { cn } from 'utils/global';

import ErrorMessage from './ErrorMessage';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  inputClassName?: string;
  labelClassName?: string;
  required?: boolean;
  className?: string;
  errorClassName?: string;
  tooltipContent?: string;
  dataCy?: string;
  requiredPlaceholder?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
}

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      labelClassName,
      onBlur,
      onFocus,
      label,
      className,
      required,
      placeholder,
      errorClassName,
      inputClassName,
      error,
      dataCy,
      requiredPlaceholder,
      isLoading,
      leftIcon,
      rightIcon,
      disabled,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    const inputStyles =
      'custom-input w-full text-xs rounded text-gray-700 placeholder:neutral-400 dark:placeholder:text-neutral-600';

    return (
      <div data-cy={dataCy} className={className}>
        {label && (
          <label
            className={cn('block text-neutral-900 dark:text-neutral-100 mb-2 text-bodyMd-semibold', labelClassName)}
          >
            {t(label)}
            {label && required && ' *'}
          </label>
        )}

        {isLoading ? (
          <Skeleton className={cn(inputClassName, inputStyles)} />
        ) : (
          <div className="relative w-full">
            {leftIcon && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none">
                {leftIcon}
              </span>
            )}

            <input
              placeholder={`${t(placeholder ? placeholder : '')} ${requiredPlaceholder ? '*' : ''}`}
              onFocus={onFocus}
              onBlur={onBlur}
              ref={ref}
              disabled={disabled}
              className={cn(
                cn(
                  '',
                  {
                    '!border-red-500': !!error,
                  },
                  { '!pl-10': leftIcon },
                  { '!pr-10': rightIcon },
                  { '!opacity-50': disabled }
                ),
                inputClassName,
                inputStyles
              )}
              {...props}
            />

            {rightIcon && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none">
                {rightIcon}
              </span>
            )}
          </div>
        )}

        {error && (
          <ErrorMessage className={errorClassName} dataCy="error-message">
            {t(error)}
          </ErrorMessage>
        )}
      </div>
    );
  }
);

export default Input;

Input.displayName = 'Input';
